import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Tags } from 'lucide-react';
import type { MediaRef, SeoMeta, TranslatableString } from '@geoic/shared';
import { Badge } from '@/components/ui/Badge.js';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Input, Select } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { ContentListPage } from '@/features/content/ContentListPage.js';
import { ContentEditPage } from '@/features/content/ContentEditPage.js';
import { SeoPanel, emptySeo } from '@/features/content/SeoPanel.js';
import { IconInput } from '@/features/blocks/BlockFields.js';
import { queryKeys } from '@/lib/query-keys.js';
import { price as formatPrice } from '@/lib/format.js';
import { AVAILABILITY_LABELS } from './VariantsEditor.js';
import {
  brandsApi,
  categoriesApi,
  productsApi,
  type BrandRecord,
  type CategoryRecord,
  type ProductRecord,
} from './shop-api.js';

// ─── Mahsulotlar ─────────────────────────────────────────────────────────────

/**
 * Katalogdagi «dan — gacha» narx oralig'i.
 *
 * Manba — `priceMin`, `hasPrice` EMAS: `hasPrice` server tomonda
 * saralash indeksining bir qismi va u eski yozuvlarda yo'q bo'lishi
 * mumkin. Narx bor-yo'qligini `priceMin` aniq aytadi.
 */
function priceRange(product: ProductRecord): string {
  if (product.priceMin === null || product.priceMin === undefined) return 'Narxni so‘rang';
  if (product.priceMin === product.priceMax) return formatPrice(product.priceMin);
  return `${formatPrice(product.priceMin)} — ${formatPrice(product.priceMax)}`;
}

export function ProductsListPage() {
  return (
    <ContentListPage<ProductRecord>
      title="Mahsulotlar"
      collection="products"
      contentApi={productsApi}
      basePath="/products"
      createLabel="Mahsulot qo‘shish"
      emptyTitle="Hozircha mahsulot yo‘q"
      emptyHint="Mahsulot qo‘shing — narx va mavjudlik komplektatsiyalardan olinadi."
      actions={
        <>
          <Link to="/categories">
            <Button variant="outline" icon={<Tags className="size-4" />}>
              Kategoriyalar
            </Button>
          </Link>
          <Link to="/brands">
            <Button variant="outline">Brendlar</Button>
          </Link>
        </>
      }
      columns={[
        {
          key: 'title',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.title?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'variants',
          header: 'Komplekt',
          align: 'right',
          width: '110px',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{row.variantCount ?? 0}</span>,
        },
        {
          key: 'price',
          header: 'Narx',
          align: 'right',
          width: '220px',
          render: (row) => <span className="text-n-600">{priceRange(row)}</span>,
        },
        {
          key: 'availability',
          header: 'Mavjudligi',
          width: '170px',
          hideOnTablet: true,
          render: (row) => (
            <Badge
              tone={
                row.availabilityAgg === 'in_stock'
                  ? 'success'
                  : row.availabilityAgg === 'on_order'
                    ? 'warning'
                    : 'neutral'
              }
            >
              {AVAILABILITY_LABELS[row.availabilityAgg ?? 'out_of_stock']}
            </Badge>
          ),
        },
      ]}
    />
  );
}

// ─── Kategoriyalar ───────────────────────────────────────────────────────────

type CategoryForm = {
  name: TranslatableString;
  slug: string;
  description: TranslatableString;
  parentId: string;
  image: MediaRef | null;
  icon: string;
  seo: SeoMeta;
};

export function CategoriesListPage() {
  return (
    <ContentListPage<CategoryRecord>
      title="Kategoriyalar"
      collection="categories"
      contentApi={categoriesApi}
      basePath="/categories"
      createLabel="Kategoriya qo‘shish"
      sortable
      emptyTitle="Hozircha kategoriya yo‘q"
      emptyHint="Tartib katalogdagi ko‘rinish tartibini belgilaydi."
      columns={[
        {
          key: 'name',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.name?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'productCount',
          header: 'Mahsulotlar',
          align: 'right',
          width: '130px',
          render: (row) => <span className="text-n-600">{row.productCount ?? 0}</span>,
        },
      ]}
    />
  );
}

export function CategoryEditPage() {
  const params = { limit: 100, sort: 'title' as const };
  const list = useQuery({
    queryKey: queryKeys.content.list('categories', { ...params, purpose: 'parent' }),
    queryFn: ({ signal }) => categoriesApi.list(params, signal),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <ContentEditPage<CategoryForm>
      collection="categories"
      contentApi={categoriesApi}
      basePath="/categories"
      backLabel="Kategoriyalar"
      newTitle="Yangi kategoriya"
      titleOf={(form) => form.name.uz}
      empty={() => ({
        name: { uz: '', ru: '', en: '' },
        slug: '',
        description: { uz: '', ru: '', en: '' },
        parentId: '',
        image: null,
        icon: '',
        seo: emptySeo(),
      })}
      fromRecord={(record) => {
        const category = record as CategoryRecord;
        return {
          name: category.name ?? { uz: '', ru: '', en: '' },
          slug: category.slug ?? '',
          description: category.description ?? { uz: '', ru: '', en: '' },
          parentId: category.parentId ?? '',
          image: category.image ?? null,
          icon: category.icon ?? '',
          seo: category.seo ?? emptySeo(),
        };
      }}
      toInput={(form) => ({
        name: form.name,
        slug: form.slug,
        description: form.description,
        parentId: form.parentId || null,
        image: form.image,
        icon: form.icon || null,
        seo: form.seo,
      })}
      renderFields={({ form, setForm, fieldErrors, record }) => (
        <div className="grid max-w-5xl gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Kategoriya" />
            <CardBody className="space-y-4">
              <TranslatableInput
                label="Nomi"
                required
                value={form.name}
                onChange={(name) => setForm((c) => ({ ...c, name }))}
                error={fieldErrors['name.uz']}
              />
              <TranslatableInput
                label="Tavsif"
                multiline
                rows={3}
                value={form.description}
                onChange={(description) => setForm((c) => ({ ...c, description }))}
              />
              <Select
                label="Yuqori kategoriya"
                value={form.parentId}
                placeholder="Yo‘q — yuqori daraja"
                // O'ZINI ota qilib tanlash mumkin emas: daraxt halqaga
                // aylanib, katalog cheksiz rekursiyaga tushardi.
                options={(list.data?.data ?? [])
                  .filter((row) => row._id !== record?._id)
                  .map((row) => ({ value: row._id, label: row.name?.uz || row.slug || row._id }))}
                onChange={(event) => setForm((c) => ({ ...c, parentId: event.target.value }))}
                hint="Katalog 2 darajali — ichki kategoriyaga yana ichki qo‘shilmaydi"
              />
              <MediaPicker
                label="Rasm"
                value={form.image}
                onChange={(image) => setForm((c) => ({ ...c, image }))}
              />
              <IconInput value={form.icon} onChange={(icon) => setForm((c) => ({ ...c, icon }))} />
              <Input
                label="Manzil (slug)"
                value={form.slug}
                onChange={(event) => setForm((c) => ({ ...c, slug: event.target.value }))}
                error={fieldErrors.slug}
              />
            </CardBody>
          </Card>

          <SeoPanel
            value={form.seo}
            onChange={(seo) => setForm((c) => ({ ...c, seo }))}
            fallbackTitle={form.name.uz || undefined}
          />
        </div>
      )}
    />
  );
}

// ─── Brendlar ────────────────────────────────────────────────────────────────

type BrandForm = {
  name: string;
  slug: string;
  logo: MediaRef | null;
  description: TranslatableString;
};

export function BrandsListPage() {
  return (
    <ContentListPage<BrandRecord>
      title="Brendlar"
      collection="brands"
      contentApi={brandsApi}
      basePath="/brands"
      createLabel="Brend qo‘shish"
      sortable
      emptyTitle="Hozircha brend yo‘q"
      columns={[
        {
          key: 'name',
          header: 'Nomi',
          render: (row) => <span className="font-medium text-n-800">{row.name || '(nomsiz)'}</span>,
        },
        {
          key: 'description',
          header: 'Tavsif',
          hideOnTablet: true,
          render: (row) => (
            <span className="line-clamp-1 text-n-600">{row.description?.uz || '—'}</span>
          ),
        },
      ]}
    />
  );
}

export function BrandEditPage() {
  return (
    <ContentEditPage<BrandForm>
      collection="brands"
      contentApi={brandsApi}
      basePath="/brands"
      backLabel="Brendlar"
      newTitle="Yangi brend"
      titleOf={(form) => form.name}
      empty={() => ({
        name: '',
        slug: '',
        logo: null,
        description: { uz: '', ru: '', en: '' },
      })}
      fromRecord={(record) => {
        const brand = record as BrandRecord;
        return {
          name: brand.name ?? '',
          slug: brand.slug ?? '',
          logo: brand.logo ?? null,
          description: brand.description ?? { uz: '', ru: '', en: '' },
        };
      }}
      toInput={(form) => ({
        name: form.name,
        slug: form.slug,
        logo: form.logo,
        description: form.description,
      })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <Card className="max-w-3xl">
          <CardHeader title="Brend" />
          <CardBody className="space-y-4">
            <Input
              label="Nomi"
              required
              value={form.name}
              placeholder="DJI"
              onChange={(event) => setForm((c) => ({ ...c, name: event.target.value }))}
              error={fieldErrors.name}
              hint="Brend nomi tarjima qilinmaydi — u hamma tilda bir xil"
            />
            <MediaPicker
              label="Logotip"
              value={form.logo}
              onChange={(logo) => setForm((c) => ({ ...c, logo }))}
            />
            <TranslatableInput
              label="Tavsif"
              multiline
              rows={3}
              value={form.description}
              onChange={(description) => setForm((c) => ({ ...c, description }))}
            />
            <Input
              label="Manzil (slug)"
              value={form.slug}
              onChange={(event) => setForm((c) => ({ ...c, slug: event.target.value }))}
              error={fieldErrors.slug}
            />
          </CardBody>
        </Card>
      )}
    />
  );
}
